import gql from "graphql-tag";
import moment, { MomentInput } from "moment-timezone";
import { State2Way } from "@/decorators";
import { Apollo } from "@/decorators";

import REVENUE_ACCOUNTABLE_ACCOUNTS_QUERY from "../../graphql/accountableAccount/RevenueAccountableAccounts.gql";
import CREATE_UPDATE_ZTILL_RECEIPT from "../../graphql/zTillReceipt/CreateUpdateZTillReceipt.gql";
import DELETE_ZTILL from "../../graphql/DeleteZTill.gql";
import DOCUMENTS_PREV_NEXT_QUERY from "../../graphql/document/PrevNextDocument.gql";
import { mathFix, isAccountingZero } from "../../services/mathServices";
import { newCommonFormatCurrency } from "../../../../backend/src/common/services/mathServices";
import { UserActivity } from "../../services/userActivityServices";
import { filterWithoutClosedAccountingPeriods } from "../../services/accountingPeriods";
import { formatOrderBy, getCommonDocumentWhere } from "../../services/graphqlServices";
import FreeTag from "../tag/FreeTag.vue";
import { TicketZBase } from "./TicketZBase";
import { getDBFormattedDate } from "@/services/dateServices";
import { filterAccountableAccounts } from "../../../../backend/src/common/accountableAccount/accountableAccountServices";
import { vatListTicketZ } from "@/services/vatServices";
import { Component } from "vue-facing-decorator";
import { Watch } from "vue-facing-decorator";
import { getNextNoValidatedDocumentId } from "../document/DocumentValidationSpeedRun.helper";

export interface Income {
    accountableAccount: string;
    vatRate: number;
    netAmount: number;
    vatAmount: number;
    grossAmount: number;
    freeAmount: number;
    vatType?: string;
    document?: {
        signedUrl?: string;
        type: string;
        tags: [];
    };
    updateTags?: [];
    accountingLine?;
}

@Component({
    components: {
        FreeTag,
    },
    emits: ["next", "previous", "delete", "created"],
})
export class TicketZEditionBase extends TicketZBase {
    colorValidButton: string = "green";
    status: boolean = false;
    alertType: "success" | "info" | "warning" | "error" = "success";
    verifDate: boolean = false;
    errorPeriodMessage: string = "";
    file: {
        fileKey: string;
        fileType: string;
        sha256: string;
        size: number;
    } = undefined;

    get selectedAccountingClosing() {
        return this.$store.state.accountingPeriod.selectedAccountingClosing?.id
            ? this.$store.state.accountingPeriod.selectedAccountingClosing
            : null;
    }

    updatePrevNext() {
        let initPrevNext = true;
        if (this.inZTillReceipt?.originalDocument) {
            if (this.prevNext && this.prevNext.currentId === this.documentId) {
                initPrevNext = false;
            }
        }
        if (initPrevNext) {
            this.prevNext = {
                currentId: "",
                prevId: "",
                nextId: "",
            };
        }
    }

    getWhere() {
        const where = getCommonDocumentWhere(
            this.selectedCompany.id,
            this.selectedAccountingClosing,
            this.filter,
            false,
        );
        return {
            companyId: this.selectedCompany.id,
            where,
            documentId: this.documentId,
            orderBy: formatOrderBy(this.filter.pagination, "type"),
        };
    }

    prevNextSmartQuery() {
        this.$apollo.addSmartQuery("prevNextDocumentId", {
            query: DOCUMENTS_PREV_NEXT_QUERY,
            variables() {
                return this.getWhere();
            },
            result: ({ data }) => {
                if (data) {
                    const nextDocumentId =
                        data.nextDocumentId && data.nextDocumentId.length > 0
                            ? data.nextDocumentId[0].id
                            : "";
                    const previousDocumentId =
                        data.previousDocumentId && data.previousDocumentId.length > 0
                            ? data.previousDocumentId[0].id
                            : "";

                    this.prevNext = {
                        currentId: this.documentId,
                        prevId: previousDocumentId,
                        nextId: nextDocumentId,
                    };
                } else {
                    this.prevNext = {
                        currentId: this.documentId,
                        prevId: "",
                        nextId: "",
                    };
                }
            },
            skip() {
                return (
                    this.mode !== "Router" ||
                    this.rootOrigin ||
                    !this.documentId ||
                    this.prevNext.currentId === this.documentId ||
                    !this.selectedCompany.id
                );
            },
        });
    }

    finishMounted() {
        this.updatePrevNext();
        if (
            this.filter.status !== "RECONCILIATION_MISSING" &&
            this.filter.status !== "RECONCILIATION_DONE"
        ) {
            this.prevNextSmartQuery();
        }
    }

    @State2Way("updateFilter", "documentFilter")
    filter!: {
        status: string;
        pagination: {
            page?: number;
            rowsPerPage?: number;
            totalItems?: number;
            sortBy?: string;
            descending: string;
        };
    };

    @State2Way("updateDocumentPrevNext", "documentFilter.prevNext")
    prevNext!: { currentId: string; prevId: string; nextId: string };

    periods: Array<{ key: string; value: string; onegram: string }> = [
        { key: "AM", value: "Matin", onegram: "MA" },
        { key: "PM", value: "Après-midi", onegram: "AP" },
        { key: "DA", value: "Journée", onegram: "JO" },
        { key: "WE", value: "Semaine", onegram: "SE" },
        { key: "MO", value: "Mois", onegram: "MO" },
        { key: "UNKNOWN", value: "Non défini", onegram: "XX" },
    ];

    /* --- Date Z de caisse variables --- */
    date: string = moment().format("YYYY-MM-DD");

    get activateTagguing() {
        return true;
    }

    get week() {
        if (this.date) {
            const date = moment(this.date);
            const startDate = date.startOf("week").format("YYYY-MM-DD");
            const endDate = date.endOf("week").format("YYYY-MM-DD");
            return [startDate, endDate];
        }
        return [];
    }

    set week(dates: MomentInput[]) {
        if (dates.length) {
            const date = moment(dates[0]);
            this.date = date.endOf("week").format("YYYY-MM-DD");
        } else {
            console.log("No date");
        }
    }

    loading = false;

    defaultVats = [
        { accountableAccount: "", netAmount: 0, grossAmount: 0, vatAmount: 0, vatRate: 5.5 },
        { accountableAccount: "", netAmount: 0, grossAmount: 0, vatAmount: 0, vatRate: 8.5 },
        { accountableAccount: "", netAmount: 0, grossAmount: 0, vatAmount: 0, vatRate: 10 },
        { accountableAccount: "", netAmount: 0, grossAmount: 0, vatAmount: 0, vatRate: 20 },
    ];

    incomes: Income[] = [];

    tags = null;

    onDateChanged(date: string) {
        this.date = date;
    }

    @Watch("period")
    @Watch("date")
    _onDateChanged() {
        this.verification();
    }

    newCommonFormatCurrency(value, options = undefined) {
        return newCommonFormatCurrency(value, options);
    }

    allowedDates(date) {
        return filterWithoutClosedAccountingPeriods(
            this.$store.state.accountingPeriod.accountingPeriods,
            date,
        );
    }

    get company() {
        return this.$store.getters.getCompany(this.companyId);
    }

    get withTransactions() {
        if (this.company && this.company.profile) {
            return !!this.company.profile.zTillReceiptWithTransactions;
        }
        return false;
    }

    get isPDF() {
        return (
            this.inZTillReceipt &&
            this.inZTillReceipt.originalDocument &&
            this.inZTillReceipt.originalDocument.original &&
            this.inZTillReceipt.originalDocument.original.type === "PDF"
        );
    }

    get isImage() {
        const imageTypes = ["PNG", "JPEG", "JPG", "TIFF", "TIF"];
        return (
            this.inZTillReceipt &&
            this.inZTillReceipt.originalDocument &&
            this.inZTillReceipt.originalDocument.original &&
            imageTypes.indexOf(this.inZTillReceipt.originalDocument.original.type) !== -1
        );
    }

    get documentId() {
        return this.inZTillReceipt?.document?.id;
    }

    //Ajouté depuis 27/11/2024. peut etre cette partie du donne n'est pas la bonne, et il faut prendre le document depuis un autre endroit.
    //TODO Please Verify. Bug -> https://trello.com/c/eVu6q5eT/975-incoherence-dans-ticketzeditionwithdocumentvue
    get document() {
        return this.inZTillReceipt?.document;
    }

    nextDisabled() {
        if (this.mode === "ToBeDone") {
            // from tobedone
            return true;
        }
        return !this.prevNext?.nextId;
    }

    previousDisabled() {
        if (this.mode === "ToBeDone") {
            // from tobedone
            return true;
        }
        return !this.prevNext?.prevId;
    }

    next() {
        if (this.mode === "ToBeDone") {
            // from tobedone
            this.$emit("next");
        } else {
            // console.log("next id", this.nextDocumentId);
            if (!this.prevNext?.nextId) {
                return;
            }
            // this.validated = false
            this.$router.replace({
                name: "document",
                params: {
                    companyId: this.companyId,
                    id: this.prevNext.nextId,
                },
            });
        }
    }

    previous() {
        if (this.mode === "ToBeDone") {
            // from tobedone
            this.$emit("previous");
        } else {
            // console.log("previous id", this.previousDocumentId);
            if (!this.prevNext?.prevId) {
                console.info("No previous document");
                return;
            }
            // this.validated = false
            this.$router.replace({
                name: "document",
                params: {
                    companyId: this.companyId,
                    id: this.prevNext.prevId,
                },
            });
        }
    }

    get errorText() {
        let displayText = "";
        if (!this.period) {
            displayText += "Période manquante. ";
        }
        if (this.creationMode === "edition" || this.creationMode === "creation") {
            for (const income of this.incomes) {
                if (income.freeAmount < 0) {
                    displayText +=
                        "Un montant offert ne peut pas être négatif : " +
                        newCommonFormatCurrency(income.freeAmount);
                } else if (income.freeAmount && income.grossAmount - income.freeAmount < 0) {
                    if (this.paidAmount <= 0) {
                        displayText +=
                            "Un montant offert est trop important : " +
                            newCommonFormatCurrency(income.freeAmount);
                    }
                }
            }
        }
        if (this.withTransactions) {
            if (this.diff) {
                displayText +=
                    "Le montant payé est différents du total des transactions écart = " +
                    newCommonFormatCurrency(this.diff);
                displayText +=
                    " Il sera compensé par " +
                    (this.diff > 0
                        ? "un produit divers de gestion courante"
                        : "une charge diverses de gestion courante");
                this.alertType = "warning";
            }
        }
        return displayText;
    }

    initializeUserActivity() {
        this.activity = new UserActivity("CREATE", "ZTILL_RECEIPT", moment());
    }

    async createUserActivity(inZTillReceiptId, action) {
        await this.activity.create(
            this.$apollo,
            this.currentUser.id,
            this.companyId,
            inZTillReceiptId,
            action,
        );
        this.initializeUserActivity();
    }

    activity: UserActivity;

    async onBeforeMountCommon() {
        this.initializeUserActivity();
        if (this.inZTillReceipt) {
            // this.date = moment(this.inZTillReceipt.date).locale("fr").format("DD MMM YYYY"); // set Day
            this.date = moment(this.inZTillReceipt.date).format("YYYY-MM-DD");
            // set Period
            this.period = this.inZTillReceipt.period;

            // this.loadIncomes();

            let commission = JSON.parse(JSON.stringify(this.defaultCommission));
            const incomes = [];
            const transactions = [];
            const additionalAmounts = [];

            if (this.inZTillReceipt.documents && this.inZTillReceipt.documents.length) {
                for (const document of this.inZTillReceipt.documents) {
                    if (
                        document.accountableAccount &&
                        document.accountableAccount.name === this.freeAmountAccountableAccount
                    ) {
                        // OBSOLETE
                        if (incomes.length) {
                            incomes[incomes.length - 1].freeAmount =
                                Math.round((document.netAmount + document.vatAmount) * 100) / 100;
                        } else {
                            console.info("incomes.lengh does not exist.");
                        }
                    } else if (document.accountableAccount) {
                        if (document.accountableAccount.name.startsWith("622200")) {
                            commission = {
                                vatRate: document.vatRate,
                                netAmount: document.netAmount,
                                vatAmount: document.vatAmount,
                                grossAmount: document.netAmount + document.vatAmount,
                                accountingLine: null,
                                document: document,
                            };
                        } else if (
                            document.accountableAccount.name.startsWith("658000") ||
                            document.accountableAccount.name.startsWith("758000")
                        ) {
                            // nothing to do
                        } else {
                            incomes.push({
                                accountableAccount: document.accountableAccount.name,
                                vatRate: document.vatRate.toString(),
                                netAmount: document.netAmount,
                                vatAmount: document.vatAmount,
                                grossAmount: document.netAmount + document.vatAmount,
                                freeAmount: document.freeAmount || 0.0,
                                document: document,
                                accountingLine: null,
                            });
                        }
                    } else if (this.withTransactions) {
                        for (const al of document.accountingLines) {
                            // const transactionType = this.aaTransactionTypes[al.accountableAccount.name];
                            // if (transactionType) {
                            transactions.push({
                                // type: transactionType,
                                name: al.accountableAccount.name,
                                amount: al.amountSigned,
                            });
                        }
                    }
                }
            } else if (this.inZTillReceipt.document) {
                const groupedALs = [];
                for (const al of this.inZTillReceipt.document.accountingLines) {
                    let group = groupedALs.find((gal) => gal.index === al.index);
                    if (!group) {
                        group = {
                            index: al.index,
                            mainAL: null,
                            coAL: null,
                            als: [],
                        };
                        groupedALs.push(group);
                    }
                    if (al.accountableAccount.name.startsWith("622200")) {
                        group.mainAL = al;
                    } else if (
                        !al.accountableAccount.name.startsWith("709") &&
                        al.accountableAccount.name.startsWith("70")
                    ) {
                        group.mainAL = al;
                    }
                    group.als.push(al);
                }
                for (const group of groupedALs) {
                    if (group.mainAL && group.als.length > 1) {
                        const netAmount = group.als.find(
                            (al) =>
                                al.accountableAccount.name === group.mainAL.accountableAccount.name,
                        ).amountSigned;
                        let vatAmount = 0;
                        let vatRate = 0;
                        let vatType = "NO_VAT_TYPE";
                        const vatAmountAl = group.als.find((al) =>
                            al.accountableAccount.name.startsWith("445"),
                        );
                        if (
                            vatAmountAl &&
                            !vatAmountAl.accountableAccount.vatType?.includes("INTRACOM")
                        ) {
                            vatAmount = vatAmountAl.amountSigned;
                            vatRate = mathFix((100.0 * vatAmount) / netAmount, 1);
                            vatRate = this.getExistingVatRate(vatRate);
                        } else {
                            vatType = "INTRACOM"; // not vat accountable account (commission)
                        }

                        if (group.mainAL.accountableAccount.name.startsWith("622200")) {
                            commission = {
                                vatRate,
                                vatType: null,
                                netAmount,
                                vatAmount,
                                grossAmount: mathFix(netAmount + vatAmount),
                                document: null,
                                accountingLine: group.mainAL,
                            };
                        } else {
                            const freeAmountAL = group.als.find(
                                (al) =>
                                    al.accountableAccount.name.startsWith("623") ||
                                    al.accountableAccount.name.startsWith("709"),
                            );
                            let freeAmount = freeAmountAL ? freeAmountAL.amountSigned : 0.0;
                            if (freeAmount) {
                                freeAmount += (vatRate * freeAmount) / 100;
                                freeAmount = mathFix(freeAmount);
                            }
                            if (vatRate !== 0) {
                                vatType = null;
                            }
                            incomes.push({
                                accountableAccount: group.mainAL.accountableAccount.name.substring(
                                    0,
                                    3,
                                ),
                                vatRate: vatRate,
                                vatType,
                                netAmount: -netAmount,
                                vatAmount: -vatAmount,
                                grossAmount: mathFix(-(netAmount + vatAmount)),
                                freeAmount: freeAmount,
                                document: null,
                                accountingLine: group.mainAL,
                            });
                        }
                    } else if (this.withTransactions) {
                        for (const al of group.als) {
                            // const transactionType = this.aaTransactionTypes[al.accountableAccount.name];
                            // if (transactionType) {
                            if (al.accountableAccount.name.startsWith("5")) {
                                transactions.push({
                                    // type: transactionType,
                                    name: al.accountableAccount.name,
                                    amount: al.amountSigned,
                                });
                            } else {
                                additionalAmounts.push({
                                    name: al.accountableAccount.name,
                                    amountSigned: al.amountSigned,
                                });
                            }
                        }
                    }
                }
            }
            this.incomes = incomes;
            this.transactions = transactions;
            this.additionalAmounts = additionalAmounts;
            this.commission = commission;
            if (this.withTransactions && !this.transactions.length) {
                this.transactions.push({ name: null, amount: 0 });
            }
        }
    }

    @Apollo({
        query() {
            return REVENUE_ACCOUNTABLE_ACCOUNTS_QUERY;
        },
        variables() {
            return {
                companyId: this.companyId,
                vatRate0: false,
            };
        },
        // debounce: 500,
        skip() {
            return !this.companyId;
        },
        specificResult({ data, loading }) {
            this.loadingDocument = loading;
            if (!loading) {
                if (data && data.companySalesAccounts && data.companySalesAccounts.salesAccounts) {
                    this.salesAccountableAccounts = data.companySalesAccounts.salesAccounts;
                    if (this.salesAccountableAccounts.length) {
                        const aaName = this.salesAccountableAccounts[0].name;
                        for (const income of this.defaultVats) {
                            income.accountableAccount = aaName;
                        }
                        if (!this.inZTillReceipt && this.creationMode === "edition") {
                            this.incomes = JSON.parse(JSON.stringify(this.defaultVats));
                        }
                    }
                } else {
                    console.log("AccountableAccountVue data pb");
                    this.salesAccountableAccounts.splice(0, this.salesAccountableAccounts.length);

                    for (const income of this.defaultVats) {
                        income.accountableAccount = "";
                    }
                    this.incomes = [];
                }
                this.initializeSalesAccountableAccount();
            }
        },
    })
    salesAccountableAccounts = [];

    get transactionDefaultAccountableAccounts() {
        if (this.$store.state.accountableAccountTable) {
            const includeRootAAs = ["531", "5111", "5115", "58"];
            const accountableAccounts = filterAccountableAccounts(
                this.$store.state.accountableAccountTable,
                includeRootAAs,
                [],
            );
            const aa511 = this.$store.state.accountableAccountTable.accountableAccounts.find(
                (aa) => aa.name === "511",
            );
            const defaultAccountableAccounts = accountableAccounts.filter(
                (aa) => (aa.default === true && aa.default !== null) || aa.name.startsWith("531"),
            );
            if (aa511) {
                defaultAccountableAccounts.push(aa511);
            }
            return defaultAccountableAccounts;
        }
        return [];
    }

    get transactionAccountableAccounts() {
        if (this.$store.state.accountableAccountTable) {
            const includeRootAAs = ["531", "5111", "5115"];
            const accountableAccounts = filterAccountableAccounts(
                this.$store.state.accountableAccountTable,
                includeRootAAs,
                [],
            );
            const aa511 = this.$store.state.accountableAccountTable.accountableAccounts.find(
                (aa) => aa.name === "511",
            );
            if (aa511) {
                accountableAccounts.push(aa511);
            }
            return accountableAccounts;
        }
        return [];
    }

    initializeSalesAccountableAccount() {}

    getAccountableAccountDescription(accountableAccount) {
        const salesAccountableAccount = this.salesAccountableAccounts.find(
            (aa) => aa.name === accountableAccount,
        );
        if (salesAccountableAccount) return salesAccountableAccount.description;
        return accountableAccount;
    }

    isIncomeValid(income) {
        if (!income.grossAmount || !income.accountableAccount) return false;
        if (income.freeAmount < 0) return false;
        if (income.grossAmount - income.freeAmount < 0) return false;
        return true;
    }

    get vatRates() {
        return vatListTicketZ;
    }

    get month() {
        return this.period === "MO";
    }

    formatDate(inputDate: string = undefined) {
        moment.locale("fr");
        if (!inputDate) {
            return moment(this.date).format(this.datePickerFormatDate);
        } else {
            return moment(inputDate).format(this.datePickerFormatDate);
        }
    }

    get datePickerFormatDate() {
        return this.month ? "MMMM YYYY" : "dddd D MMMM YYYY";
    }

    getExistingVatRate(tva) {
        const vatRate = this.vatRates.find((vt) => Math.abs(vt.value - tva) < 1e-2);
        if (vatRate) {
            return vatRate.value;
        }
        let minDiff = 1e20;
        let bestTVA = tva;
        for (let i = 0; i < this.vatRates.length; i++) {
            const diff = Math.abs(tva - this.vatRates[i].value);
            if (diff < minDiff) {
                minDiff = diff;
                bestTVA = this.vatRates[i];
            }
        }
        if (isAccountingZero(minDiff)) {
            return bestTVA.value;
        }
        return null;
    }

    /* --- TVA Functions --- */

    computeOtherAmounts(income, addCategory: boolean = true) {
        income.netAmount = mathFix(income.grossAmount / (1 + income.vatRate / 100));
        income.vatAmount = mathFix(income.grossAmount - income.netAmount);
        if (addCategory) {
            this.verifValidation();
            this.addCategory(income);
        }
    }

    TTCPrice(income, addCategory: boolean = true) {
        this.computeOtherAmounts(income, addCategory);
    }

    onFreeAmountChanged() {
        this.verifValidation();
    }

    VATRate(income: Income, eventValue: string | number, addCategory: boolean = true) {
        if (eventValue !== null) {
            if (typeof eventValue === "string") {
                if (eventValue.startsWith("0") && eventValue.length > 1) {
                    income.vatType = eventValue.slice(1);
                    income.vatRate = 0;
                }
            } else {
                income.vatType = eventValue !== 0 ? null : "NO_VAT_TYPE";
                income.vatRate = eventValue;
            }
        }
        this.computeOtherAmounts(income, addCategory);
    }

    deleteCategory(income: Income) {
        if (
            (this.creationMode === "edition" && this.incomes.length > 1) ||
            (this.creationMode === "creation" && this.incomes.length)
        ) {
            const index = this.incomes.indexOf(income);
            if (index >= 0 && index < this.incomes.length) {
                this.incomes.splice(index, 1);
                this.verifValidation();
            }
        }
    }

    addCategory(income) {
        // const income = this.incomes[this.incomes.length - 1];
        if (this.incomes.indexOf(income) === this.incomes.length - 1) {
            if (!income.accountableAccount) return;
            if (!income.vatRate) return;
            if (income.netAmount === 0) return;
            if (income.vatAmount === 0) return;
            if (income.grossAmount === 0) return;
            this.incomes.push({
                accountableAccount: "",
                vatRate: 0,
                netAmount: 0,
                vatAmount: 0,
                grossAmount: 0,
                freeAmount: 0,
            });
        }
    }

    get difference() {
        let difference = 0.0;
        if (this.withTransactions) {
            let transactionSum = 0;
            for (const transaction of this.transactions) {
                transactionSum += Number(transaction.amount);
            }
            difference = this.paidAmount - transactionSum;
        }

        return difference;
    }

    get diff() {
        return Math.round(this.difference * 100) / 100;
    }

    verifValidation() {
        // let sumTTC = 0
        this.status = true;
        this.alertType = "success";

        if (!this.period) {
            // this.errorText += "Période manquante. ";
            this.alertType = "warning";
            this.status = false;
        }

        if (this.creationMode === "edition" || this.creationMode === "creation") {
            for (const income of this.incomes) {
                if (income.freeAmount < 0) {
                    this.alertType = "warning";
                    this.status = false;
                } else if (income.freeAmount && income.grossAmount - income.freeAmount < 0) {
                    if (this.paidAmount <= 0) {
                        this.alertType = "warning";
                        this.status = false;
                    }
                }
            }
            if (this.withTransactions) {
                // this.diff = Math.round( this.difference * 100) / 100
                if (this.diff) {
                    this.alertType = "warning";
                }
            }

            this.colorValidButton = this.diff === 0 ? "green" : "grey";

            if (this.paidAmount === 0) {
                this.status = false;
            }
        }
    }

    /**
     * Fonction de vérification
     */
    async verification() {
        this.verifValidation();
        if (this.date && this.period && (!this.inZTillReceipt || !this.inZTillReceipt.id)) {
            if ((await this.getPeriodAlreadyInDB()) === true) {
                this.verifDate = false;
                this.errorPeriodMessage = "";
            } else {
                this.verifDate = true;
                this.errorPeriodMessage = `La période du ${this.date} ${this.period} est déja dans la base de données`;
            }
        }
    }

    async deleteZTill() {
        await this.$apollo.mutate({
            mutation: DELETE_ZTILL,
            variables: {
                zTillId: this.inZTillReceipt.id,
            },
        });
        try {
            await this.createUserActivity(this.inZTillReceipt.id, "DELETE"); // "UPDATE"
        } catch (e) {
            console.error(e);
        }
        if (this.mode === "Router") this.$router.go(-1);
        else this.$emit("deleted");
    }

    /**
     * Procédure aprés validation
     */
    async valid() {
        if (this.status) {
            this.loading = true;

            let date = getDBFormattedDate(this.date);

            if (this.period === "MO") {
                date = getDBFormattedDate(moment(this.date).endOf("month"));
            }

            let incomes;
            for (const income of this.incomes) {
                if (this.isItemValid(income)) {
                    if (!incomes) {
                        incomes = [];
                    }
                    incomes.push(income);
                }
            }

            let file;
            if (this.file && this.file.fileKey) {
                file = {
                    key: this.file.fileKey,
                    type: this.file.fileType,
                    sha256: this.file.sha256,
                    fileSize: this.file.size,
                };
            }

            let tags = undefined;
            if (this.tags && (this.tags.toCreateTags.length || this.tags.toAddTags.length)) {
                tags = this.tags;
                console.log("Tags", tags);
            }

            const result = await this.$apollo.mutate({
                mutation: CREATE_UPDATE_ZTILL_RECEIPT,
                variables: {
                    zTillReceiptId: this.inZTillReceipt ? this.inZTillReceipt.id : "",
                    file: file,
                    userId: this.currentUser.id,
                    companyId: this.companyId,
                    date,
                    period: this.period,
                    incomes: incomes,
                    status: this.inZTillReceipt
                        ? "VALIDATED"
                        : incomes && incomes.length
                          ? "VALIDATED"
                          : "ANALYSED",
                    commission:
                        this.commission && this.commission.grossAmount
                            ? this.commission
                            : undefined,
                    transactions: this.withTransactions
                        ? this.transactions.filter((t) => t.amount !== 0)
                        : undefined,
                    difference: this.withTransactions ? this.difference : undefined,
                    paidAmount: this.paidAmount,
                    totalNetAmount: this.totalNetAmount,
                    totalVatAmount: this.totalVatAmount,
                    totalGrossAmount: this.totalGrossAmount,
                    tags,
                },
            });

            if (result && result.data.createUpdateZTillReceipt) {
                // @todo vincent
                try {
                    await this.createUserActivity(
                        result.data.createUpdateZTillReceipt.id,
                        "UPDATE",
                    );
                } catch (e) {
                    console.error(e);
                }
            }

            if (this.inZTillReceipt && this.inZTillReceipt.id) {
                // this.$router.go(-1);
            } else {
                this.reset();
                this.$emit("created");
            }
            this.loading = false;

            const store_documentParams = this.$store.state?.documentFilter ?? {};
            if (store_documentParams?.switchToNextNoValidatedDocumentAfterValidation) {
                const nextDocumentId = await getNextNoValidatedDocumentId(
                    this.$apollo,
                    this.getWhere(),
                );
                if (nextDocumentId?.length) {
                    if (nextDocumentId === "_") {
                        this.lastTicketZValidationDone = true;
                    } else {
                        this.prevNext.nextId = nextDocumentId;
                        this.next();
                    }
                }
            }
        }
    }

    /**
     * Nombre de jours dans un mois d'une année
     * @param {number} month
     * @param {number} year
     * @returns {number}
     */

    getDaysInMonth(month: number, year: number) {
        return month === 2
            ? year & 3 || (!(year % 25) && year & 15)
                ? 28
                : 29
            : 30 + ((month + (month >> 3)) & 1);
    }

    /**
     * Reset de la fenetre
     */
    reset() {
        this.incomes = JSON.parse(JSON.stringify(this.defaultVats));

        this.period = "MO";
        this.date = moment().endOf("month").startOf("day").format("YYYY-MM-DD");

        this.transactions = [
            { name: "511120", amount: 0 },
            { name: "531000", amount: 0 },
            { name: "511130", amount: 0 },
        ];

        this.additionalAmounts = [];
    }

    /**
     * Vérification de l'existance du Z de caisse
     */
    async getPeriodAlreadyInDB() {
        const command = `zTillReceipts(where: { company:{id: "${
            this.companyId
        }"} date: "${getDBFormattedDate(this.date)}", period: ${this.period}}) { id }`;
        const periodInDB = await this.$apollo.query({
            query: gql(`query{${command}}`),
        });
        if (periodInDB.data.zTillReceipts.length === 0) {
            return true;
        }
        return false;
    }

    /* --- Transactions --- */

    transactionsHeaders = [
        { title: "Type de transaction", value: "vatRate", sortable: false },
        { title: "Montant", value: "netAmount", sortable: false },
        { title: "Supprimer", value: "grossAmount", sortable: false },
    ];

    aaTransactionTypes = {
        "531000": "Espèces (531)",
        "511100": "Espèces (511)",
        "511110": "Chèque",
        "511120": "Carte de crédit",
        "511130": "Virement",
        "511140": "Ticket restaurant",
        "511141": "Chèque vacances",
        "511150": "Paypal",
        "511160": "Plateforme (Deliveroo, Uber eats .. )",
        "511": "Inconnu",
    };

    aaTransactionCodes = {
        CASH: "Remise espèces",
        CHEQUE: "Remise chèques",
        CREDIT_CARD: "Remise cartes bleues",
        TRANSFER: "Remise virement",
        LUNCHEON_VOUCHER: "Remise tickets restaurant",
        HOLIDAY_VOUCHER: "Remise chèque vacances",
        PAYPAL: "Remise Paypal",
        DISTRIBUTION_PLATFORM: "Remise Plateforme (Deliveroo, Uber eats .. )",
        CREDIT_NOTES_ISSUED: "Remise - Avoirs émis",
        CREDIT_NOTES_RECOVERY: "Remise - Reprise d'avoirs",
        FREE: "Libre",
    };

    transactions: Array<{ name: string; amount: number }> = [
        { name: "511120", amount: 0 },
        { name: "531000", amount: 0 },
        { name: "511110", amount: 0 },
    ];

    getDescription(aa: { name: string; description: string; transactionType: string }) {
        return (
            this.aaTransactionTypes[aa.name] ||
            this.aaTransactionCodes[aa.transactionType] ||
            aa.description
        );
    }

    /**
     * Suppression de la transaction
     * @param {object} transaction
     */

    deleteTransaction(transaction) {
        if (this.transactions.length > 1) {
            const index = this.transactions.indexOf(transaction);
            if (index >= 0 && index < this.transactions.length) {
                this.transactions.splice(index, 1);
            }
        }
        this.verifValidation();
    }

    /**
     * Suppression de montant additionel
     * @param {object} additionalAmounts
     */
    deleteAdditionalAmount(additionalAmount: { name: string; amountSigned: number }) {
        if (this.additionalAmounts.length >= 1) {
            const index = this.additionalAmounts.indexOf(additionalAmount);
            if (index >= 0 && index < this.additionalAmounts.length) {
                this.additionalAmounts.splice(index, 1);
            }
        }
        this.verifValidation();
    }

    /**
     * Fonction : ajouter d'une transaction
     */

    addTransaction() {
        this.transactions.push({ name: null, amount: 0 });
        this.verifValidation();
    }

    addAdditionalAmount() {
        this.additionalAmounts.push({ name: null, amountSigned: 0 });
        this.verifValidation();
    }

    lastTicketZValidationDone = false;
}
